
import { PathLayer } from '@deck.gl/layers';

/**
 * Generates a Deck.gl PathLayer for the vessel's trailing path.
 * 
 * @param {Array|Array[]} dataTracks - The full vessel data sorted by time, or an array of distinct tracks.
 * @param {number} currentTime - The current playback timestamp.
 * @param {boolean} visible - Whether the layer is visible.
 * @returns {PathLayer[]|null}
 */
export const generateTrailingPathLayer = (dataTracks, currentTime, visible) => {
    if (!dataTracks || dataTracks.length === 0 || !currentTime || !visible) return null;

    // Detect if we have a single track or an array of tracks
    const tracks = (dataTracks[0] && Array.isArray(dataTracks[0])) ? dataTracks : [dataTracks];
    const layers = [];

    tracks.forEach((track, trackIdx) => {
        if (track.length < 2) return;

        let currentIndex = -1;
        let low = 0, high = track.length - 1;
        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            const t = new Date(track[mid].time || track[mid].timestamp).getTime();
            if (t <= currentTime) {
                currentIndex = mid;
                low = mid + 1;
            } else {
                high = mid - 1;
            }
        }

        if (currentIndex < 1) return;

        // Requirement says 8-10 points. Let's do 10.
        const trailLength = 10;
        const startIndex = Math.max(0, currentIndex - trailLength + 1);
        const segments = [];

        for (let i = startIndex; i < currentIndex; i++) {
            const p1 = track[i];
            const p2 = track[i + 1];

            const pos = i - startIndex;
            const total = currentIndex - startIndex;
            const ratio = total > 0 ? pos / total : 1;

            const isThreat = p1.threat_detected === 'Yes' || p1.threat_detected === true;
            const rgb = isThreat ? [239, 68, 68] : [0, 200, 80];
            const alpha = Math.round(25 + ratio * (230 - 25));

            segments.push({
                path: [[p1.lon, p1.lat], [p2.lon, p2.lat]],
                color: [...rgb, alpha]
            });
        }

        layers.push(
            new PathLayer({
                id: `vessel-trail-layer-${trackIdx}`,
                data: segments,
                pickable: false,
                widthUnits: 'pixels',
                widthMinPixels: 3,
                widthMaxPixels: 5,
                getPath: d => d.path,
                getColor: d => d.color,
                getWidth: 3,
                capRounded: true,
                jointRounded: true,
                opacity: 1,
                visible,
                updateTriggers: {
                    getColor: [currentTime],
                    getPath: [currentTime]
                }
            })
        );
    });

    return layers;
};
